import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import { STATIC__FILES_HOSTNAME } from '@/constants'
import { useAccountStore } from '@/stores/useAccountStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCubeStore } from '@/stores/useCubeStore'
import { useCurrentReadsStore } from '@/stores/useCurrentReadsStore'
import { useMapStore } from '@/stores/useMapStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import type { AppConfigResponse } from '@/types/CubeDto'
import { NumberUtil } from '@/util/NumberUtil'
import { setProductionTypes as setProductionTypesUtil } from '@/util/Strings'
import { type AxiosResponse } from "axios"
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { type Router } from 'vue-router'

export const useAppStore = defineStore('app', () => {
  const { logError } = useLogError()
  const { backendApi, strapiApi } = useHttpClient()

  // --- State ---
  const isLoading = ref(true)
  const appInitialized = ref(false)
  const showNavigation = ref(true)
  const currentRouteName = ref<string | null>(null)
  const maintenanceMessage = ref<string | undefined>(undefined)
  const serviceWorkerRegistration = ref<ServiceWorkerRegistration | false>(false)

  const slideins = reactive<Record<string, { visible: boolean }>>({
    menu: { visible: false },
    notifications: { visible: false },
    friends: { visible: false },
  })

  // App config — populated from getAppConfig response in init()
  const googleMapsApiKey = ref<string>(import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY_OVERRIDE ?? '')
  const googleMapsMapId = ref<string>(import.meta.env.VITE_APP_DEFAULT_GOOGLE_MAP_ID ?? '')
  const googleMapsMapIdPreview = ref<string>(import.meta.env.VITE_APP_DEFAULT_GOOGLE_MAP_ID_Preview ?? '')
  const googleMapsStaticApiBaseUrl = ref<string>('')
  const tos = reactive({ url: new URL('/app/v2/legal/efriends-nutzungsbedingungen-2021-11-02.pdf', STATIC__FILES_HOSTNAME).toString(), version: 20211102 })
  const privacy = reactive({ url: 'https://www.efriends.at/datenschutz.html', version: 20211102 })
  const providers = ref<any[]>([])
  const customerInfo = ref<Record<string, any> | null>(null)
  // Static — hardcoded in backend config, never changes at runtime
  const contactData = { phone: '0043295320102', phoneFormatted: '02953/20102', email: 'support@efriends.at' }

  // --- Computed ---
  const isSlideinVisible = computed(() => (slidein?: string) => {
    if (!slidein) { return Object.values(slideins).some(s => s.visible) }
    return slideins[slidein]?.visible ?? false
  })

  function showSlidein(slidein: string, show?: boolean | 'toggle') {
    const current = slideins[slidein]?.visible ?? false
    const next = show === 'toggle' || show === undefined ? !current : show

    if (slideins[slidein]) {
      slideins[slidein].visible = next
    }
  }

  function hideAllSlideins() {
    Object.keys(slideins).forEach(k => { slideins[k] = { ... slideins[k], visible: false }})
  }

  // --- Actions ---

  async function updateSessionSettings(settings: Record<string, any>) {
    const auth = useAuthStore()
    return await backendApi.put(`/app/SessionSettings/${auth.backendAccessToken}`, settings)
  }

  async function selectCube({ cube, currentRoute }: { cube: string | { cubeName: string }; currentRoute: any }) {
    if (!cube) {
      throw new Error('selectCube called with invalid cube.')
    }
    const cubeName = typeof cube === 'string' ? cube : cube.cubeName
    const reloadApp = typeof cube === 'string'

    try {
      const result = await updateSessionSettings({ selectedCubeName: cubeName })

      if (result && result.data.bSuccess && reloadApp) {
        if ('redirect' in currentRoute.query) {
          window.location.href = currentRoute.query.redirect
        } else if (currentRouteName.value === 'login') {
          window.location.href = '/'
        } else {
          window.location.reload()
        }
      }
    } catch (err) {
      logError('Error selecting cube: ' + err)
    }
  }

  async function getMaintenanceMessage() {
    try {
      const response = await strapiApi.get('/api/maintenance-message')
      const data = response.data?.data
      if (data) {
        const from = new Date(data.from)
        const to = new Date(data.to)
        const now = new Date()
        const text = from <= now && now <= to ? data.text : undefined
        maintenanceMessage.value = text
      }
    } catch {
      // fail silently — maintenance message is non-critical
    }
  }

  let maintenanceInterval: ReturnType<typeof setInterval> | null = null

  async function init({ router, subsequent = false }: { router: Router; subsequent?: boolean }) {
    const auth = useAuthStore()
    const accountStore = useAccountStore()

    document.getElementById('app')?.setAttribute('data-loaded', 'true')

    // Only set up the maintenance interval on first init, not on subsequent long-poll calls.
    if (!subsequent) {
      getMaintenanceMessage()
      if (!maintenanceInterval) {
        maintenanceInterval = setInterval(() => getMaintenanceMessage(), 10 * 60 * 1000)
      }
    }

    if (!auth.backendAccessToken) { return }

    try {
      const appConfigResponse: AxiosResponse<AppConfigResponse> = await backendApi.get('/app/getAppConfig', {
        params: { requestCurrentRead: true },
      })

      // getAppSettings — blocking, needed before showing the app.
      await accountStore.getAppSettings()

      const accessObjects = appConfigResponse.data.data?.cubeAuthData?.accessObjects ?? appConfigResponse.data.data?.accessObjects ?? {};
      const accessObjectsKey = Object.keys(accessObjects)?.[0]
      const cubeName = accountStore.availableCubes?.[0]?.cubeName ?? accessObjectsKey;

      if (!appConfigResponse.data.data.cubeSelected) {
        accountStore.setAvailableCubes(accessObjects)

        if (accountStore.availableCubes.length <= 1 && cubeName) {
          await selectCube({
            cube: cubeName,
            currentRoute: router.currentRoute.value,
          })
        } else {
        // } else if (router.currentRoute.value.name !== 'select-cube') {
          isLoading.value = false
          router.push({ name: 'select-cube', hash: router.currentRoute.value.hash })
        }
      } else if (appConfigResponse.data.data.cubeAuthData) {
        accountStore.setAvailableCubes(appConfigResponse.data.data.cubeAuthData.accessObjects)

        // Populate Pinia account + cube stores from the same response.
        accountStore.setFromAppConfig(appConfigResponse.data)
        const cubeStore = useCubeStore()
        cubeStore.setFromAppConfig(appConfigResponse.data)
        cubeStore.setSupplierData(appConfigResponse.data.data.customerInfo.supplierServiceAgreement)

        // Populate app config state.
        const appConfig = appConfigResponse.data.data.appConfig

        if (!googleMapsApiKey.value) { googleMapsApiKey.value = appConfig.googleMapsApiKey ?? '' }
        if (appConfig?.googleMapsMapId) { googleMapsMapId.value = appConfig.googleMapsMapId }

        googleMapsStaticApiBaseUrl.value = appConfig.googleMapsStaticApiBaseUrl ?? ''

        if ('tosUrl' in appConfig) { tos.url = appConfig.tosUrl ?? "" }
        if ('tosVersion' in appConfig) { tos.version = NumberUtil.parse(appConfig.tosVersion) ?? 0 }
        if ('privacyUrl' in appConfig) { privacy.url = appConfig.privacyUrl ?? "" }
        if ('privacyVersion' in appConfig) { privacy.version = parseInt(appConfig.privacyVersion ?? "0") }

        providers.value = appConfigResponse.data.data.generalData.serviceSuppliers
        customerInfo.value = appConfigResponse.data.data.customerInfo

        if (serviceWorkerRegistration.value) {
          // Lazy import to avoid circular dependency (useNotificationsStore → useAppStore).
          const { useNotificationsStore } = await import('@/stores/useNotificationsStore')
          useNotificationsStore().setupPushNotifications()
        }

        useCurrentReadsStore().meterDataReceived(appConfigResponse.data.data.currentRead)

        // Mark the app ready.
        appInitialized.value = true
        isLoading.value = false

        // Non-blocking — fire and forget.
        cubeStore.updateCubeConnections()
        useMapStore().loadGlobalCubeList()

        if (!accountStore.isDemoUser) {
          if (!accountStore.properties.isCommunityless) {
            cubeStore.setupMeterSocket()
          }

          useSettingsStore().loadConsumerPrices()
          useSettingsStore().loadContractData()
          useSettingsStore().loadDefaultSellingPrice()
          useMapStore().getEfriendsOfTheMonth()
        }

        setProductionTypesUtil(appConfigResponse.data.data.productionTypes)
      } else {
        console.error("No valid authentication/accessObjects received", appConfigResponse);

        throw new Error('Got no valid app config/authentication infos.')
      }
    } catch (err) {
      logError('The initializeApp request failed: ' + err)
      useAuthStore().clearAuth()
      router.push({ name: 'login' })
    }
  }

  function refresh({ router }: { router: Router }) {
    return init({ router, subsequent: true })
  }

  // Called by useAuthStore.clearAuth() on logout.
  function reset() {
    appInitialized.value = false
    isLoading.value = true
    maintenanceMessage.value = undefined
    currentRouteName.value = null
    hideAllSlideins()
    providers.value = []
    customerInfo.value = null
    if (maintenanceInterval) {
      clearInterval(maintenanceInterval)
      maintenanceInterval = null
    }
  }

  return {
    // State
    isLoading,
    appInitialized,
    showNavigation,
    currentRouteName,
    maintenanceMessage,
    serviceWorkerRegistration,
    slideins,
    googleMapsApiKey,
    googleMapsMapId,
    googleMapsStaticApiBaseUrl,
    googleMapsMapIdPreview,
    tos,
    privacy,
    providers,
    customerInfo,
    contactData,
    // Computed
    isSlideinVisible,
    // Actions
    showSlidein,
    hideAllSlideins,
    // Actions
    selectCube,
    getMaintenanceMessage,
    init,
    refresh,
    reset,
  }
})
